import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { format, addDays, isBefore, startOfDay, isSameDay, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/style.css';
import { Calendar, User, Mail, Phone, MessageSquare, CheckCircle, PawPrint } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const properties = [
  { id: 'cabana-concept', name: 'Cabana Concept', maxGuests: 2, price: 1250 },
  { id: 'cabana-aurora', name: 'Cabana Aurora', maxGuests: 2, price: 1160 },
  { id: 'casa-m5', name: 'Casa M5', maxGuests: 10, price: 2560 }
];

const BookingSection = () => {
  const [selectedProperty, setSelectedProperty] = useState('cabana-concept');
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [blockedDates, setBlockedDates] = useState([]);
  const [formData, setFormData] = useState({
    guestName: '',
    guestCpf: '',
    guestBirthDate: '',
    guestEmail: '',
    guestPhone: '',
    companionName: '',
    guests: 2,
    hasPets: false,
    paymentMethod: 'pix',
    installments: 1,
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [confirmedBooking, setConfirmedBooking] = useState(null);

  // Calculate total value
  const calculateTotal = () => {
    if (!dateRange?.from || !dateRange?.to) return null;
    
    const selectedPropertyData = properties.find(p => p.id === selectedProperty);
    if (!selectedPropertyData) return null;
    
    const days = Math.ceil((dateRange.to - dateRange.from) / (1000 * 60 * 60 * 24));
    const dailyRate = selectedPropertyData.price;
    const subtotal = days * dailyRate;
    const petFee = formData.hasPets ? 200 : 0;
    const total = subtotal + petFee;
    
    return { days, dailyRate, subtotal, petFee, total };
  };

  const totalInfo = calculateTotal();

  useEffect(() => {
    loadBlockedDates();
  }, [selectedProperty]);

  const loadBlockedDates = async () => {
    try {
      const response = await axios.get(`${API}/availability/${selectedProperty}`);
      const dates = response.data.blocked_dates.map(dateStr => parseISO(dateStr));
      setBlockedDates(dates);
    } catch (error) {
      console.error('Error loading availability:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!dateRange?.from || !dateRange?.to) {
      setSubmitError('Por favor, selecione as datas de check-in e check-out.');
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const propertyName = properties.find(p => p.id === selectedProperty)?.name;
      
      const totalCalc = calculateTotal();
      
      await axios.post(`${API}/bookings`, {
        property_name: propertyName,
        guest_name: formData.guestName,
        guest_cpf: formData.guestCpf,
        guest_birth_date: formData.guestBirthDate,
        guest_email: formData.guestEmail,
        guest_phone: formData.guestPhone,
        companion_name: formData.companionName || null,
        check_in: format(dateRange.from, 'yyyy-MM-dd'),
        check_out: format(dateRange.to, 'yyyy-MM-dd'),
        guests: formData.guests,
        has_pets: formData.hasPets,
        payment_method: formData.paymentMethod,
        installments: formData.paymentMethod === 'credit_card' ? formData.installments : null,
        total_days: totalCalc?.days,
        daily_rate: totalCalc?.dailyRate,
        total_amount: totalCalc?.total,
        message: formData.message
      });

      // Save confirmed booking data for success screen
      setConfirmedBooking({
        propertyName: propertyName,
        total: totalCalc?.total,
        checkIn: dateRange.from,
        checkOut: dateRange.to
      });

      setSubmitSuccess(true);
      setFormData({
        guestName: '',
        guestCpf: '',
        guestBirthDate: '',
        guestEmail: '',
        guestPhone: '',
        companionName: '',
        guests: 2,
        hasPets: false,
        paymentMethod: 'pix',
        installments: 1,
        message: ''
      });
      setDateRange({ from: null, to: null });
    } catch (error) {
      console.error('Error submitting booking:', error);
      setSubmitError('Erro ao enviar solicitação. Por favor, tente novamente ou entre em contato via WhatsApp.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNewBooking = () => {
    setSubmitSuccess(false);
    setConfirmedBooking(null);
  };

  const disabledDays = [
    ...blockedDates,
    { before: startOfDay(new Date()) }
  ];

  const selectedPropertyData = properties.find(p => p.id === selectedProperty);

  // Success Screen
  if (submitSuccess && confirmedBooking) {
    return (
      <section id="booking" className="py-24 md:py-32 px-6 bg-muted min-h-screen">
        {/* Success Banner */}
        <div className="fixed top-0 left-0 right-0 z-50 bg-primary text-white py-4 px-6">
          <div className="max-w-6xl mx-auto flex items-center justify-center gap-3">
            <CheckCircle size={24} />
            <span className="font-semibold">Sua solicitação foi concluída com sucesso!</span>
          </div>
        </div>

        <div className="max-w-2xl mx-auto pt-16">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            {/* Large Check Icon */}
            <div className="w-24 h-24 bg-primary rounded-full flex items-center justify-center mx-auto mb-8">
              <CheckCircle size={48} className="text-white" />
            </div>

            {/* Title */}
            <h2 className="text-3xl md:text-4xl font-heading text-primary mb-6">
              Sua solicitação foi concluída com sucesso!
            </h2>

            {/* Description */}
            <p className="text-lg text-muted-foreground mb-12 max-w-lg mx-auto">
              Recebemos seus dados e nossa equipe entrará em contato em breve pelo WhatsApp para confirmar todos os detalhes da sua estadia.
            </p>

            {/* Booking Summary Cards */}
            <div className="grid grid-cols-2 gap-4 mb-12">
              <div className="bg-white rounded-2xl p-6 text-left border border-border">
                <p className="text-xs tracking-[0.15em] uppercase text-muted-foreground mb-2">CABANA</p>
                <p className="text-lg font-semibold text-primary">{confirmedBooking.propertyName}</p>
              </div>
              <div className="bg-white rounded-2xl p-6 text-left border border-border">
                <p className="text-xs tracking-[0.15em] uppercase text-muted-foreground mb-2">TOTAL</p>
                <p className="text-lg font-semibold text-primary">R$ {confirmedBooking.total?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
              </div>
              <div className="bg-white rounded-2xl p-6 text-left border border-border">
                <p className="text-xs tracking-[0.15em] uppercase text-muted-foreground mb-2">CHECK-IN</p>
                <p className="text-lg font-semibold text-primary">
                  {confirmedBooking.checkIn ? format(confirmedBooking.checkIn, "d 'de' MMMM 'de' yyyy", { locale: ptBR }) : '-'}
                </p>
              </div>
              <div className="bg-white rounded-2xl p-6 text-left border border-border">
                <p className="text-xs tracking-[0.15em] uppercase text-muted-foreground mb-2">CHECK-OUT</p>
                <p className="text-lg font-semibold text-primary">
                  {confirmedBooking.checkOut ? format(confirmedBooking.checkOut, "d 'de' MMMM 'de' yyyy", { locale: ptBR }) : '-'}
                </p>
              </div>
            </div>

            {/* New Booking Button */}
            <button
              data-testid="new-booking-btn"
              onClick={handleNewBooking}
              className="bg-accent text-white hover:bg-accent/90 rounded-full px-10 py-5 text-sm uppercase tracking-widest transition-all hover:scale-105"
            >
              Fazer nova solicitação
            </button>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section id="booking" className="py-24 md:py-32 px-6 bg-muted">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-accent mb-4">
            Reserve Sua Estadia
          </p>
          <h2 className="text-4xl md:text-5xl font-light tracking-tight text-primary mb-6">
            Solicitar Reserva
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Preencha o formulário abaixo e entraremos em contato para confirmar sua reserva.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="bg-white rounded-3xl shadow-deep p-8 md:p-12"
        >
          {submitError && (
            <div 
              data-testid="booking-error-message"
              className="mb-8 p-6 bg-red-50 border border-red-200 rounded-2xl"
            >
              <p className="text-sm text-red-600">{submitError}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Property Selection */}
            <div>
              <label className="block text-sm font-semibold text-primary mb-4">
                Selecione a Acomodação
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {properties.map(property => (
                  <button
                    key={property.id}
                    type="button"
                    data-testid={`property-select-${property.id}`}
                    onClick={() => {
                      setSelectedProperty(property.id);
                      setDateRange({ from: null, to: null });
                    }}
                    className={`p-6 rounded-2xl border-2 transition-all text-left ${
                      selectedProperty === property.id
                        ? 'border-accent bg-accent/5'
                        : 'border-border hover:border-accent/50'
                    }`}
                  >
                    <h3 className="font-semibold text-primary mb-2">{property.name}</h3>
                    <p className="text-sm text-muted-foreground mb-3">Até {property.maxGuests} pessoas</p>
                    <p className="text-lg font-semibold text-accent">
                      R$ {property.price.toLocaleString('pt-BR')} <span className="text-sm font-normal text-muted-foreground">/diária</span>
                    </p>
                  </button>
                ))}
              </div>
            </div>

            {/* Date Range Picker */}
            <div>
              <label className="block text-sm font-semibold text-primary mb-4">
                Selecione as Datas
              </label>
              {dateRange?.from && (
                <div className="text-center mb-4 text-sm text-muted-foreground">
                  {dateRange.to ? (
                    <>
                      De <strong>{format(dateRange.from, 'dd/MM/yyyy', { locale: ptBR })}</strong> até{' '}
                      <strong>{format(dateRange.to, 'dd/MM/yyyy', { locale: ptBR })}</strong>
                    </>
                  ) : (
                    <>Selecione a data de check-out</>
                  )}
                </div>
              )}
              <div className="flex justify-center">
                <DayPicker
                  mode="range"
                  selected={dateRange}
                  onSelect={setDateRange}
                  disabled={disabledDays}
                  locale={ptBR}
                  numberOfMonths={2}
                  className="border border-border rounded-2xl p-4"
                  modifiersStyles={{
                    selected: {
                      backgroundColor: '#B08D55',
                      color: 'white'
                    }
                  }}
                />
              </div>
              
              {/* Total Calculation */}
              {totalInfo && (
                <div className="mt-6 bg-accent/10 border border-accent/20 rounded-2xl p-6">
                  <h4 className="text-lg font-semibold text-primary mb-4">Resumo da Reserva</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{totalInfo.days} {totalInfo.days === 1 ? 'diária' : 'diárias'} × R$ {totalInfo.dailyRate.toLocaleString('pt-BR')}</span>
                      <span className="font-semibold">R$ {totalInfo.subtotal.toLocaleString('pt-BR')}</span>
                    </div>
                    {formData.hasPets && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Taxa de pet</span>
                        <span className="font-semibold">R$ 200</span>
                      </div>
                    )}
                    <div className="border-t border-accent/20 pt-2 mt-2">
                      <div className="flex justify-between text-lg">
                        <span className="font-bold text-primary">Total</span>
                        <span className="font-bold text-accent">R$ {totalInfo.total.toLocaleString('pt-BR')}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Guest Information */}
            <div>
              <h3 className="text-xl font-semibold text-primary mb-4 border-b border-border pb-2">
                Dados do Responsável pela Reserva
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-primary mb-3">
                    <User size={16} className="inline mr-2" />
                    Nome Completo *
                  </label>
                  <input
                    data-testid="booking-name-input"
                    type="text"
                    required
                    value={formData.guestName}
                    onChange={(e) => setFormData({ ...formData, guestName: e.target.value })}
                    className="w-full px-4 py-3 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-accent"
                    placeholder="Seu nome completo"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-primary mb-3">
                    CPF *
                  </label>
                  <input
                    data-testid="booking-cpf-input"
                    type="text"
                    required
                    value={formData.guestCpf}
                    onChange={(e) => setFormData({ ...formData, guestCpf: e.target.value })}
                    className="w-full px-4 py-3 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-accent"
                    placeholder="000.000.000-00"
                    maxLength="14"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-primary mb-3">
                    Data de Nascimento *
                  </label>
                  <input
                    data-testid="booking-birthdate-input"
                    type="date"
                    required
                    value={formData.guestBirthDate}
                    onChange={(e) => setFormData({ ...formData, guestBirthDate: e.target.value })}
                    className="w-full px-4 py-3 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-accent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-primary mb-3">
                    <Mail size={16} className="inline mr-2" />
                    Email *
                  </label>
                  <input
                    data-testid="booking-email-input"
                    type="email"
                    required
                    value={formData.guestEmail}
                    onChange={(e) => setFormData({ ...formData, guestEmail: e.target.value })}
                    className="w-full px-4 py-3 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-accent"
                    placeholder="seu@email.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-primary mb-3">
                    <Phone size={16} className="inline mr-2" />
                    Telefone/WhatsApp *
                  </label>
                  <input
                    data-testid="booking-phone-input"
                    type="tel"
                    required
                    value={formData.guestPhone}
                    onChange={(e) => setFormData({ ...formData, guestPhone: e.target.value })}
                    className="w-full px-4 py-3 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-accent"
                    placeholder="(22) 99999-9999"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-primary mb-3">
                    <User size={16} className="inline mr-2" />
                    Número de Hóspedes *
                  </label>
                  <select
                    data-testid="booking-guests-select"
                    required
                    value={formData.guests}
                    onChange={(e) => setFormData({ ...formData, guests: parseInt(e.target.value) })}
                    className="w-full px-4 py-3 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-accent"
                  >
                    {Array.from({ length: selectedPropertyData?.maxGuests || 2 }, (_, i) => i + 1).map(num => (
                      <option key={num} value={num}>{num} {num === 1 ? 'pessoa' : 'pessoas'}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Companion Information */}
            <div>
              <h3 className="text-xl font-semibold text-primary mb-4 border-b border-border pb-2">
                Dados do(a) Acompanhante
              </h3>
              <div className="grid md:grid-cols-1 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-primary mb-3">
                    <User size={16} className="inline mr-2" />
                    Nome Completo do(a) Acompanhante (Opcional)
                  </label>
                  <input
                    data-testid="booking-companion-input"
                    type="text"
                    value={formData.companionName}
                    onChange={(e) => setFormData({ ...formData, companionName: e.target.value })}
                    className="w-full px-4 py-3 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-accent"
                    placeholder="Nome do acompanhante"
                  />
                </div>
              </div>
            </div>

            {/* Payment Information */}
            <div>
              <h3 className="text-xl font-semibold text-primary mb-4 border-b border-border pb-2">
                Forma de Pagamento
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-primary mb-3">
                    Forma de Pagamento *
                  </label>
                  <select
                    data-testid="booking-payment-select"
                    required
                    value={formData.paymentMethod}
                    onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
                    className="w-full px-4 py-3 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-accent"
                  >
                    <option value="pix">PIX</option>
                    <option value="credit_card">Cartão de Crédito</option>
                  </select>
                </div>

                {formData.paymentMethod === 'credit_card' && (
                  <div>
                    <label className="block text-sm font-semibold text-primary mb-3">
                      Parcelamento
                    </label>
                    <select
                      data-testid="booking-installments-select"
                      required
                      value={formData.installments}
                      onChange={(e) => setFormData({ ...formData, installments: parseInt(e.target.value) })}
                      className="w-full px-4 py-3 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-accent"
                    >
                      <option value="1">À vista</option>
                      <option value="2">2x sem juros</option>
                      <option value="3">3x sem juros</option>
                      <option value="4">4x sem juros</option>
                      <option value="5">5x sem juros</option>
                      <option value="6">6x sem juros</option>
                    </select>
                  </div>
                )}
              </div>
            </div>

            {/* Pets */}
            <div>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  data-testid="booking-pets-checkbox"
                  type="checkbox"
                  checked={formData.hasPets}
                  onChange={(e) => setFormData({ ...formData, hasPets: e.target.checked })}
                  className="w-5 h-5 text-accent border-border rounded focus:ring-accent"
                />
                <span className="text-sm text-primary">
                  <PawPrint size={16} className="inline mr-2" />
                  Vou levar meu pet (taxa adicional de R$ 200,00)
                </span>
              </label>
            </div>

            {/* Message */}
            <div>
              <label className="block text-sm font-semibold text-primary mb-3">
                <MessageSquare size={16} className="inline mr-2" />
                Mensagem (Opcional)
              </label>
              <textarea
                data-testid="booking-message-input"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                rows={4}
                className="w-full px-4 py-3 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-accent resize-none"
                placeholder="Alguma solicitação especial ou dúvida?"
              />
            </div>

            {/* Submit Button */}
            <button
              data-testid="booking-submit-btn"
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-accent text-white hover:bg-accent/90 rounded-full px-8 py-6 text-sm uppercase tracking-widest transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Enviando...' : 'Solicitar Reserva'}
            </button>

            <p className="text-xs text-center text-muted-foreground">
              Ao enviar, você concorda em receber contato via email ou WhatsApp para confirmação da reserva.
            </p>
          </form>
        </motion.div>
      </div>
    </section>
  );
};

export default BookingSection;